import { useMemo } from "react";
import {
  Alert,
  Badge,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowDown,
  IconArrowUp,
  IconScale,
  IconTrophy,
} from "@tabler/icons-react";
import {
  LuaBlock,
  PresetCallout,
  useStepRuleSet,
} from "./components/SharedRuleParts";
import { AGGREGATION_LABELS, TIEBREAKER_LABELS } from "./types";

const SCORING_SKELETON_HEIGHTS = [28, 56, 120, 180] as const;

export const ScoringEditor = () => {
  const { ruleSet, fallback } = useStepRuleSet({
    title: "Scoring Rules",
    icon: <IconScale size={20} />,
    skeletonHeights: SCORING_SKELETON_HEIGHTS,
  });

  const scoring = ruleSet?.scoringRuleSet ?? null;

  const sortedTiebreakers = useMemo(
    () =>
      scoring ? [...scoring.tiebreakers].sort((a, b) => a.order - b.order) : [],
    [scoring],
  );

  if (!ruleSet) return fallback;

  if (!scoring) {
    return (
      <Stack>
        <Group gap="xs">
          <IconScale size={20} />
          <Title order={2}>Scoring Rules</Title>
        </Group>
        <Alert
          color="yellow"
          icon={<IconAlertCircle size={16} />}
          variant="light"
        >
          This rule set has no scoring configured. Apply a preset to configure
          scoring.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack>
      <Group gap="xs">
        <IconScale size={20} />
        <Title order={2}>Scoring Rules</Title>
      </Group>

      <PresetCallout />

      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600}>Aggregation</Text>
            <Badge variant="light" color="grape" size="lg">
              {AGGREGATION_LABELS[scoring.aggregation]}
            </Badge>
          </Group>

          {scoring.aggregation === "FORMULA" && (
            <LuaBlock
              label="Score Formula"
              value={scoring.formula}
              maxHeight={260}
            />
          )}

          {scoring.aggregation === "POINTS" && (
            <Group grow>
              <Card withBorder padding="sm">
                <Text size="xs" c="dimmed">
                  Win
                </Text>
                <Text fw={700}>{scoring.winPoints}</Text>
              </Card>
              <Card withBorder padding="sm">
                <Text size="xs" c="dimmed">
                  Loss
                </Text>
                <Text fw={700}>{scoring.lossPoints}</Text>
              </Card>
              <Card withBorder padding="sm">
                <Text size="xs" c="dimmed">
                  Draw
                </Text>
                <Text fw={700}>{scoring.drawPoints}</Text>
              </Card>
              <Card withBorder padding="sm">
                <Text size="xs" c="dimmed">
                  Forfeit
                </Text>
                <Text fw={700}>{scoring.forfeitPoints}</Text>
              </Card>
            </Group>
          )}
        </Stack>
      </Card>

      <Divider
        labelPosition="left"
        label={
          <Group gap="xs">
            <IconTrophy size={16} />
            <Text fw={600}>Tiebreakers</Text>
          </Group>
        }
      />

      {sortedTiebreakers.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No tiebreakers configured
        </Text>
      ) : (
        <Stack gap="sm">
          {sortedTiebreakers.map((tb) => (
            <Card withBorder key={`${tb.order}-${tb.type}`}>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Group gap="xs">
                    <Badge size="sm" variant="filled" color="gray">
                      #{tb.order + 1}
                    </Badge>
                    <Text fw={600}>{TIEBREAKER_LABELS[tb.type]}</Text>
                  </Group>
                  <Badge
                    variant="light"
                    color={tb.sortOrder === "DESC" ? "teal" : "blue"}
                    leftSection={
                      tb.sortOrder === "DESC" ? (
                        <IconArrowDown size={12} />
                      ) : (
                        <IconArrowUp size={12} />
                      )
                    }
                  >
                    {tb.sortOrder === "DESC" ? "Descending" : "Ascending"}
                  </Badge>
                </Group>

                {tb.type === "CUSTOM_FORMULA" && (
                  <LuaBlock
                    label="Formula"
                    value={tb.formula}
                    maxHeight={200}
                  />
                )}

                {tb.type !== "CUSTOM_FORMULA" && tb.formula && (
                  <LuaBlock
                    label="Override formula"
                    value={tb.formula}
                    maxHeight={200}
                  />
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
